import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Spinner } from '@rentflowdev/ui'
import { useTranslation } from 'react-i18next'
import { useCompleteAvitoOauthMutation } from '@/api'

/**
 * Avito OAuth callback. Avito redirects here with `?code=...&state=...` after the user
 * approves access. We exchange the code for tokens via the gateway, then redirect back
 * to the integrations page where the new connection appears.
 */
export const Route = createFileRoute('/oauth/avito/callback')({
	component: AvitoCallbackPage,
})

function readCallbackParams(): { code?: string; state?: string; error?: string } {
	if (typeof window === 'undefined') return {}
	const params = new URLSearchParams(window.location.search)
	return {
		code:  params.get('code')  ?? undefined,
		state: params.get('state') ?? undefined,
		error: params.get('error') ?? undefined,
	}
}

function AvitoCallbackPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [completeAvito] = useCompleteAvitoOauthMutation()

	const initialParams = useMemo(() => readCallbackParams(), [])
	const initialError = useMemo(
		() =>
			initialParams.error ??
			(!initialParams.code || !initialParams.state ? 'missing_code_or_state' : null),
		[initialParams],
	)
	const [error, setError] = useState<string | null>(initialError)
	const ran = useRef(false)

	useEffect(() => {
		if (ran.current || initialError) return
		ran.current = true

		completeAvito({ code: initialParams.code as string, state: initialParams.state as string })
			.unwrap()
			.then(() => {
				void navigate({ to: '/settings/integrations' })
			})
			.catch((err) => {
				console.error('completeAvitoOauth failed:', err)
				setError(typeof err?.data === 'string' ? err.data : 'oauth_exchange_failed')
			})
	}, [completeAvito, navigate, initialError, initialParams])

	return (
		<div className="flex h-screen flex-col items-center justify-center gap-3">
			{error ? (
				<>
					<p className="text-sm text-(--color-destructive)">{t('common.error')}: {error}</p>
					<button
						type="button"
						className="text-sm text-(--color-info) hover:underline"
						onClick={() => void navigate({ to: '/settings/integrations' })}
					>
						{t('common.back')}
					</button>
				</>
			) : (
				<>
					<Spinner />
					<p className="text-sm text-(--color-muted-foreground)">{t('common.loading')}</p>
				</>
			)}
		</div>
	)
}
